import { useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import { clearPassiveApiActivity, recordPassiveApiActivity } from '../services/passiveApiActivity';
import {
  createProcessingActivityEventId,
  getProcessingActivityDestination,
  InteractiveLoginEvent,
  ProcessingActivityDestination,
  ProcessingActivityReason,
  subscribeProcessingUploadIntent,
} from '../services/processingActivity';

const INTERACTION_INTERVAL_MS = 30_000;

interface ProcessingActivityOptions {
  userId: string | null;
  pageEntryReady: boolean;
  workspaceReady: boolean;
  pathname: string;
  search: string;
  interactiveLoginEvent?: InteractiveLoginEvent | null;
  onInteractiveLoginConsumed?: (sequence: number) => void;
  mcpEnabled?: boolean;
  advisoryEnabled?: boolean;
}

const isForeground = (): boolean => document.visibilityState === 'visible' && document.hasFocus();

const getPageEntryDestination = (options: ProcessingActivityOptions): ProcessingActivityDestination | null => (
  options.pageEntryReady
    ? getProcessingActivityDestination(
      options.pathname,
      options.search,
      // MCP retains account/entitlement admission; ordinary processing pages
      // can overlap account bootstrap with API and LOW worker startup.
      options.workspaceReady && options.mcpEnabled !== false,
    )
    : null
);

/** One observer for the current app surface, independent of retained hidden tabs. */
export const useProcessingActivity = (options: ProcessingActivityOptions): void => {
  const { onInteractiveLoginConsumed } = options;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const recordRef = useRef<((reason: ProcessingActivityReason, destination: ProcessingActivityDestination) => void) | null>(null);
  const lastLoginRef = useRef<InteractiveLoginEvent | null>(null);
  const enteredDestinationRef = useRef<string | null>(null);
  const enteredPageRef = useRef<string | null>(null);

  useEffect(() => {
    const userId = options.userId;
    enteredDestinationRef.current = null;
    enteredPageRef.current = null;
    if (!userId) {
      lastLoginRef.current = null;
      clearPassiveApiActivity();
      return;
    }
    let disposed = false;
    let lastSentAt = -Infinity;
    let lastAttempt = 0;
    let pending: ProcessingActivityDestination | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const requests = new Set<AbortController>();

    const cancelPending = () => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      pending = null;
    };
    const destination = () => {
      const current = optionsRef.current;
      return current.workspaceReady && current.userId === userId
        ? getProcessingActivityDestination(current.pathname, current.search, current.mcpEnabled)
        : null;
    };
    const send = (reason: ProcessingActivityReason, target: ProcessingActivityDestination) => {
      if (disposed || optionsRef.current.userId !== userId || !isForeground() || optionsRef.current.advisoryEnabled === false) return;
      cancelPending();
      lastSentAt = Date.now();
      const attempt = ++lastAttempt;
      const controller = new AbortController();
      requests.add(controller);
      const failed = () => {
        if (!disposed && attempt === lastAttempt) {
          lastSentAt = -Infinity;
          cancelPending();
        }
      };
      try {
        void apiService.recordProcessingActivity({
          event_id: createProcessingActivityEventId(), reason, ...target,
        }, userId, controller.signal).then((response) => {
          if (response.wake_status === 'failed' || response.reason === 'unavailable') failed();
        }).catch(failed).finally(() => { requests.delete(controller); });
      } catch {
        requests.delete(controller);
        failed();
      }
    };
    const record = (reason: ProcessingActivityReason, target: ProcessingActivityDestination) => {
      if (!isForeground()) return;
      recordPassiveApiActivity();
      const remaining = INTERACTION_INTERVAL_MS - (Date.now() - lastSentAt);
      if (reason !== 'interaction' || remaining <= 0) {
        send(reason, target);
        return;
      }
      // A trailing flush exists only because another real interaction occurred.
      pending = target;
      if (timer === null) timer = setTimeout(() => {
        timer = null;
        const current = destination();
        const targetToSend = pending;
        pending = null;
        if (current && targetToSend && JSON.stringify(current) === JSON.stringify(targetToSend)) {
          send('interaction', current);
        }
      }, remaining);
    };
    recordRef.current = record;
    const onInteraction = (event: Event) => {
      if (!event.isTrusted || !isForeground()) return;
      if (optionsRef.current.workspaceReady) recordPassiveApiActivity();
      const target = destination();
      if (target) record('interaction', target);
    };
    const onForegroundChange = () => {
      if (!isForeground()) {
        cancelPending();
        requests.forEach((request) => request.abort());
        enteredDestinationRef.current = null;
        enteredPageRef.current = null;
        return;
      }
      if (optionsRef.current.workspaceReady && enteredPageRef.current !== optionsRef.current.pathname) {
        enteredPageRef.current = optionsRef.current.pathname;
        recordPassiveApiActivity();
      }
      const target = getPageEntryDestination(optionsRef.current);
      if (target && enteredDestinationRef.current !== JSON.stringify(target)) {
        enteredDestinationRef.current = JSON.stringify(target);
        record('page_entry', target);
      }
    };
    const unsubscribeUpload = subscribeProcessingUploadIntent((expectedUserId) => {
      if (expectedUserId && expectedUserId !== userId) return;
      const target = destination();
      if (target) record('upload_intent', target);
    });
    ['click', 'keydown', 'input'].forEach((type) => document.addEventListener(type, onInteraction, true));
    document.addEventListener('visibilitychange', onForegroundChange);
    window.addEventListener('blur', onForegroundChange);
    window.addEventListener('focus', onForegroundChange);
    return () => {
      disposed = true;
      cancelPending();
      requests.forEach((request) => request.abort());
      recordRef.current = null;
      unsubscribeUpload();
      ['click', 'keydown', 'input'].forEach((type) => document.removeEventListener(type, onInteraction, true));
      document.removeEventListener('visibilitychange', onForegroundChange);
      window.removeEventListener('blur', onForegroundChange);
      window.removeEventListener('focus', onForegroundChange);
    };
  }, [options.userId]);

  useEffect(() => {
    if (!options.userId || !options.workspaceReady || !isForeground()) return;
    if (enteredPageRef.current === options.pathname) return;
    enteredPageRef.current = options.pathname;
    recordPassiveApiActivity();
  }, [options.userId, options.workspaceReady, options.pathname]);

  useEffect(() => {
    if (!options.userId) return;
    const login = options.interactiveLoginEvent;
    if (login?.userId !== options.userId || (login?.userId === lastLoginRef.current?.userId
      && login?.sequence === lastLoginRef.current?.sequence)) return;
    lastLoginRef.current = login;
    // Consume in the auth owner so a later shell remount cannot replay an old login.
    onInteractiveLoginConsumed?.(login.sequence);
    if (!isForeground()) return;
    recordRef.current?.('sign_in', getProcessingActivityDestination(
      options.pathname, options.search, options.mcpEnabled,
    ) || { surface: 'playground' });
  }, [options.userId, options.interactiveLoginEvent, options.pathname, options.search, options.mcpEnabled, onInteractiveLoginConsumed]);

  const target = getPageEntryDestination(options);
  const destinationKey = target ? JSON.stringify(target) : null;
  useEffect(() => {
    if (!options.userId || !destinationKey || !isForeground()) {
      enteredDestinationRef.current = null;
      return;
    }
    if (enteredDestinationRef.current === destinationKey) return;
    enteredDestinationRef.current = destinationKey;
    recordRef.current?.('page_entry', JSON.parse(destinationKey));
  }, [options.userId, destinationKey]);
};
